import { useNavigate } from 'react-router-dom'

import { Dialog } from '@factorialco/f0-react/dist/experimental'
import { Delete } from '@factorialco/f0-react/icons/app'
import { DeleteFormTemplateDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  templateId: string
  onClose: () => void
}

const DeleteFormTemplate = ({ templateId, onClose }: Props) => {
  const navigate = useNavigate()

  const onCancel = () => {
    onClose()
  }
  const [deleteFormTemplate] = useMutation(DeleteFormTemplateDocument, {
    extractorFn: (data) => data.formsMutations.deleteFormsForm,
    update(cache, { data }) {
      const deletedForm = data?.formsMutations.deleteFormsForm.form
      if (!deletedForm) return

      const normalizedId = cache.identify({
        id: deletedForm.id,
        __typename: 'FormsForm',
      })

      // Remove reference from parent list
      cache.modify({
        id: `FormsForm:${deletedForm.id}`,
        fields: {
          forms(existingRefs = []) {
            return existingRefs.filter((ref: { __ref?: string }) => {
              if (!ref.__ref) return true
              const isTarget = ref.__ref === normalizedId
              return !isTarget
            })
          },
        },
      })

      // Evict from the cache
      if (normalizedId) {
        cache.evict({ id: normalizedId })
        cache.gc()
      }
    },
  })

  const onDelete = async (id: string) => {
    const response = await deleteFormTemplate({
      variables: {
        id: id ?? '',
      },
    })
    const handled = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('forms.custom_templates.data_collection.delete_custom_template.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('forms.custom_templates.data_collection.delete_custom_template.success'),
      })
      onClose()
    }
  }

  return (
    <Dialog
      open
      onClose={onCancel}
      header={{
        title: i18n.t('forms.custom_templates.data_collection.delete_custom_template.title'),
        description: i18n.t(
          'forms.custom_templates.data_collection.delete_custom_template.description'
        ),
        type: 'critical',
      }}
      actions={{
        primary: {
          icon: Delete,
          label: i18n.t('forms.custom_templates.data_collection.delete_custom_template.delete'),
          variant: 'critical',
          onClick: async () => {
            onDelete(templateId)
          },
        },
        secondary: {
          label: i18n.t('forms.custom_templates.data_collection.delete_custom_template.cancel'),
          onClick: () => onCancel(),
        },
      }}
    />
  )
}

export default DeleteFormTemplate
